import React, { useState } from 'react';
import { Mail, Clock, MessageSquare, Bug, Shield, Handshake, Check, Copy } from 'lucide-react';
import { SeoHead } from '../components/common/SeoHead';
import { Button } from '../components/ui/Button';

export const ContactPage: React.FC = () => {
  const [copied, setCopied] = useState(false);
  const contactEmail = 'contact@kapitalletter.com';

  const handleCopy = () => {
    navigator.clipboard.writeText(contactEmail);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const channels = [
    {
      icon: MessageSquare,
      title: '일반 문의 및 기능 제안',
      desc: '서비스 이용 중 궁금한 점이나 새로운 기능에 대한 아이디어가 있으시다면 언제든 제안해 주세요.',
      subject: '[일반 문의] Kapitalletter 문의',
    },
    {
      icon: Bug,
      title: '앱 오류 및 버그 제보',
      desc: '앱 사용 중 비정상 종료나 오작동을 겪으셨다면 기기 모델 및 OS 버전과 함께 제보해 주시면 빠르게 수정합니다.',
      subject: '[버그 제보] 앱 오류 리포트',
    },
    {
      icon: Shield,
      title: '개인정보 및 계정 삭제 요청',
      desc: '개인정보 처리 및 계정 데이터 영구 삭제를 원하실 경우 접수해 주시면 관련 법령에 따라 즉시 파기합니다.',
      subject: '[개인정보 요청] 계정 및 데이터 삭제 신청',
    },
    {
      icon: Handshake,
      title: '비즈니스 및 제휴 문의',
      desc: '협업 제휴, 보도 자료, 비즈니스 협력 관련 제안을 환영합니다.',
      subject: '[비즈니스 제휴] 협업 제안',
    },
  ];

  return (
    <div className="py-12 sm:py-20 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
      <SeoHead
        title="공식 문의하기 | Contact Us"
        description="Kapitalletter 공식 문의 창구입니다. 앱 버그 제보, 계정 삭제, 비즈니스 제휴 및 일반 문의를 접수하세요."
        canonicalPath="/contact"
      />

      {/* Header */}
      <div className="space-y-3">
        <span className="text-xs font-bold text-primary-700 tracking-wider uppercase">
          Official Contact
        </span>
        <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
          궁금한 점이나 도움이 필요하신가요?
        </h1>
        <p className="text-base sm:text-lg text-slate-600">
          모든 문의는 개발팀에서 직접 확인하며, 영업일 기준 24~48시간 이내에 성심껏 회신드립니다.
        </p>
      </div>

      {/* Primary Contact Bar */}
      <div className="bg-white border border-slate-200/90 rounded-2xl p-6 sm:p-8 shadow-xs flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
        <div className="space-y-1">
          <span className="text-xs font-medium text-slate-400 block uppercase tracking-wider">
            공식 대표 이메일
          </span>
          <p className="text-xl sm:text-2xl font-mono font-bold text-slate-900">
            {contactEmail}
          </p>
          <div className="flex items-center gap-2 text-xs text-slate-500 pt-1">
            <Clock className="w-3.5 h-3.5 text-slate-400" />
            <span>월~금 운영 (한국 표준시 KST 기준)</span>
          </div>
        </div>

        <div className="flex items-center gap-3 w-full sm:w-auto">
          <a href={`mailto:${contactEmail}`} className="w-full sm:w-auto">
            <Button variant="primary" size="md" fullWidth icon={<Mail className="w-4 h-4" />}>
              이메일 작성하기
            </Button>
          </a>
          <button
            type="button"
            onClick={handleCopy}
            className="p-2.5 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-600 hover:text-slate-900 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
            title="이메일 주소 복사"
            aria-label="이메일 주소 복사"
          >
            {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* Inquiry Categories Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        {channels.map((ch, idx) => {
          const IconComponent = ch.icon;
          return (
            <div
              key={idx}
              className="bg-white border border-slate-200/90 rounded-2xl p-6 shadow-xs flex flex-col justify-between"
            >
              <div>
                <div className="w-10 h-10 rounded-xl bg-slate-100 text-slate-700 flex items-center justify-center mb-4">
                  <IconComponent className="w-5 h-5" />
                </div>
                <h3 className="font-bold text-slate-900 text-base mb-1.5">
                  {ch.title}
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed">
                  {ch.desc}
                </p>
              </div>

              <div className="pt-5 mt-4 border-t border-slate-100">
                <a
                  href={`mailto:${contactEmail}?subject=${encodeURIComponent(ch.subject)}`}
                  className="text-xs font-semibold text-primary-700 hover:text-primary-800 transition-colors inline-flex items-center gap-1"
                >
                  이메일 바로 쓰기 →
                </a>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
